import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import zolaLogo from "./zola-black.png";

export default function NewNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProductOpen, setMobileProductOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileOpen((prev) => !prev);
    setMobileProductOpen(false);
    setMobileIndustriesOpen(false);
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 bg-white ${
        scrolled ? "shadow-md" : ""
      }`}
    >
      <div className="w-full max-w-7xl mx-auto flex items-center justify-between py-4 px-6 md:px-0">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <Link
            to="/"
            aria-label="Home"
            className="flex items-center gap-2 shrink-0"
          >
            <img src={zolaLogo} alt="Zola logo" className="h-6 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-900">
            {/* Product */}
            <div className="relative group">
              <div className="flex items-center gap-1 hover:text-indigo-600 cursor-pointer">
                <span>Product</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <div className="absolute left-1/2 mt-2 w-72 bg-white shadow-lg rounded-xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 transform -translate-x-1/2">
                <Link
                  to="/reviews"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  Reviews
                </Link>
                <Link
                  to="/nps"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  NPS & CSAT
                </Link>
                <Link
                  to="/ai-surveys"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  Surveys
                </Link>
                <Link
                  to="/coupons"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  Coupons
                </Link>
                <Link
                  to="/referrals"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  Referral
                </Link>
                <Link
                  to="/team-performance"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  Team Performance
                </Link>
              </div>
            </div>

            {/* Industries */}
            <div className="relative group">
              <div className="flex items-center gap-1 hover:text-indigo-600 cursor-pointer">
                <span>Industries</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <div className="absolute left-1/2 mt-2 w-72 bg-white shadow-lg rounded-xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 transform -translate-x-1/2">
                <Link
                  to="/real-estate"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  Real Estate
                </Link>
                <Link
                  to="/restaurant"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  Restaurant
                </Link>
                <Link
                  to="/beauty"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  Beauty
                </Link>
                <Link
                  to="/home-maintenance"
                  className="block px-4 py-2 hover:bg-gray-100 hover:rounded-lg"
                >
                  Home Maintenance
                </Link>
              </div>
            </div>

            <Link to="/pricing" className="hover:text-indigo-600">
              Pricing
            </Link>
          </nav>
        </div>

        {/* CTA Buttons */}
        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/login"
            className="font-semibold text-black hover:text-indigo-600"
          >
            Log in
          </Link>
          <Link
            to="/account-type"
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 py-2 rounded-xl transition"
          >
            Sign up
          </Link>
        </div>

        {/* Hamburger Icon */}
        <div className="md:hidden">
          <button
            onClick={toggleMobileMenu}
            className="text-gray-900 focus:outline-none"
            aria-label="Toggle menu"
          >
            <motion.div
              initial={false}
              animate={mobileOpen ? "open" : "closed"}
              className="w-6 h-6 relative"
            >
              <motion.span
                className="absolute top-1 left-0 w-full h-0.5 bg-black"
                variants={{
                  open: { rotate: 45, y: 6 },
                  closed: { rotate: 0, y: 0 },
                }}
                transition={{ duration: 0.3 }}
              />
              <motion.span
                className="absolute top-2.5 left-0 w-full h-0.5 bg-black"
                variants={{
                  open: { opacity: 0 },
                  closed: { opacity: 1 },
                }}
                transition={{ duration: 0.3 }}
              />
              <motion.span
                className="absolute top-4 left-0 w-full h-0.5 bg-black"
                variants={{
                  open: { rotate: -45, y: -6 },
                  closed: { rotate: 0, y: 0 },
                }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white px-6 py-6 space-y-4 border-t border-gray-200 h-screen overflow-y-auto">
          <nav className="space-y-4">
            {/* Product Toggle */}
            <div>
              <button
                className="w-full flex justify-between items-center text-base font-medium text-gray-900 hover:text-indigo-600 py-1.5"
                onClick={() => setMobileProductOpen(!mobileProductOpen)}
              >
                Product
                <span
                  className={`transition-transform duration-300 ${
                    mobileProductOpen ? "rotate-180" : ""
                  }`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </span>
              </button>

              <AnimatePresence>
                {mobileProductOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden space-y-2"
                  >
                    {["Reviews", "NPS & CSAT", "Surveys", "Coupons"].map(
                      (item) => (
                        <a
                          key={item}
                          href="#"
                          className="block text-base font-medium text-gray-900 hover:text-indigo-600 py-1.5"
                        >
                          {item}
                        </a>
                      )
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Industries Toggle */}
            <div>
              <button
                className="w-full flex justify-between items-center text-base font-medium text-gray-900 hover:text-indigo-600 py-1.5"
                onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
              >
                Industries
                <span
                  className={`transition-transform duration-300 ${
                    mobileIndustriesOpen ? "rotate-180" : ""
                  }`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </span>
              </button>

              <AnimatePresence>
                {mobileIndustriesOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden space-y-2"
                  >
                    {["Dental", "Chiropractic", "Med Spa"].map((item) => (
                      <a
                        key={item}
                        href="#"
                        className="block text-base font-medium text-gray-900 hover:text-indigo-600 py-1.5"
                      >
                        {item}
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/pricing"
              className="block text-base font-medium text-gray-900 hover:text-indigo-600 py-1.5"
            >
              Pricing
            </Link>
          </nav>

          {/* Buttons */}
          <div className="pt-6 space-y-3">
            <Link
              to="/account-type"
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 py-2 rounded-xl transition"
            >
              Sign up
            </Link>
            <Link
              to="/login"
              className="block text-center text-gray-900 bg-white rounded-lg px-5 py-3 font-medium text-base border border-gray-300 hover:bg-gray-100"
            >
              Log in
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
